/** @jsxImportSource @emotion/react */
import React from "react";
import { Alert, Col, Form, Input, Modal, Row, Button } from "antd";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import * as Styles from "./styles";
import {
  TenantUsecase,
  useTenantDispatch,
  useTenantState,
} from "../../../usecases/Tenant";
import { GetTenantDto } from "../../../services/Tenant/dto/get.dto";

interface IFormInputs {
  tenantName: string;
  tenantEmail: string;
  tenantAddress: string;
}

const formSchema = yup.object().shape({
  tenantName: yup.string().required("Please enter last name"),
  tenantEmail: yup.string().required(),
  tenantAddress: yup.string().required("Please enter first name"),
});

interface ModalEditCompanyProfileProps {
  onCancel: () => void;
  onSuccess: () => void;
  visible: boolean;
  item: GetTenantDto;
}

const ModalEditCompanyProfileComponent: React.FC<ModalEditCompanyProfileProps> =
  ({ visible, onCancel, onSuccess, item }: ModalEditCompanyProfileProps) => {
    const [isProcessing, setIsProcessing] = React.useState(false);
    const [errorMessage, setErrorMessage] = React.useState("");
    const {
      handleSubmit,
      formState: { errors },
      control,
      reset,
    } = useForm<IFormInputs>({
      mode: "onChange",
      defaultValues: {
        tenantName: item.tenantName,
        tenantEmail: item.tenantEmail,
        tenantAddress: item.tenantAddress,
      } as IFormInputs,
      resolver: yupResolver(formSchema),
    });
    const tenantState = useTenantState();
    const tenantDispatch = useTenantDispatch();
    const tenantId = tenantState.detail?.id;

    const handleSubmitHandle = React.useCallback(
      async (data: IFormInputs) => {
        setIsProcessing(true);
        try {
          if (tenantDispatch && tenantId) {
            const tenantUsecase = new TenantUsecase(tenantDispatch);
            await tenantUsecase.update({
              id: tenantId,
              tenantName: data.tenantName,
              tenantAddress: data.tenantAddress,
            });
          }

          reset();
          setErrorMessage("");
          onSuccess();
        } catch (error) {
          console.error(error);
          setErrorMessage(error.message);
        } finally {
          setIsProcessing(false);
        }
      },
      [tenantId, tenantDispatch, onSuccess, reset],
    );

    return (
      <Modal
        title="Edit Company Profile"
        visible={visible}
        closable={false}
        footer={[
          <Button key="back" size="large" onClick={onCancel}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            size="large"
            loading={isProcessing}
            onClick={handleSubmit(handleSubmitHandle)}
          >
            Save
          </Button>,
        ]}
      >
        <Form layout="vertical" onFinish={handleSubmit(handleSubmitHandle)}>
          {errorMessage && (
            <Row justify="center">
              <Col css={Styles.colGutterStyle}>
                <Alert message={errorMessage} type="error" />
              </Col>
            </Row>
          )}
          <Form.Item
            label="Company Name"
            css={Styles.labelStyle}
            validateStatus={errors.tenantName ? "error" : "validating"}
            help={errors.tenantName?.message}
          >
            <Controller
              name="tenantName"
              control={control}
              render={({ field }) => <Input {...field} size="large" />}
            />
          </Form.Item>
          <Form.Item
            label="Contact Email"
            css={Styles.labelStyle}
            validateStatus={errors.tenantEmail ? "error" : "validating"}
            help={errors.tenantEmail?.message}
          >
            <Controller
              name="tenantEmail"
              control={control}
              render={({ field }) => <Input {...field} size="large" disabled />}
            />
          </Form.Item>
          <Form.Item
            label="Address"
            css={Styles.labelStyle}
            validateStatus={errors.tenantAddress ? "error" : "validating"}
            help={errors.tenantAddress?.message}
          >
            <Controller
              name="tenantAddress"
              control={control}
              render={({ field }) => <Input {...field} size="large" />}
            />
          </Form.Item>
        </Form>
      </Modal>
    );
  };

export const ModalEditCompanyProfile = React.memo(
  ModalEditCompanyProfileComponent,
);
